import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import MapView from "../components/MapView";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { api, getUser, isAdminUser } from "../lib/api";
import { toast } from "sonner";
import { MapPin, ArrowLeft, Recycle, Trash2, Flag, CheckCircle2, X as XIcon, Send } from "lucide-react";
import ChatPanel from "../components/ChatPanel";
import ReportDialog from "../components/ReportDialog";
import BlockUserButton from "../components/BlockUserButton";

const CONDITION_LABEL = {
  new: "New",
  like_new: "Like new",
  used: "Used — good",
  well_used: "Well used",
};

const STATUS_PILL = {
  available: { cls: "bg-[#4A6B56] text-white", label: "Available" },
  reserved: { cls: "bg-[#D67B54] text-white", label: "Reserved" },
  taken: { cls: "bg-[#1B3624] text-white", label: "Taken" },
  expired: { cls: "bg-[#EBE8E0] text-[#6B6965]", label: "Expired" },
  hidden: { cls: "bg-red-100 text-red-700", label: "Hidden" },
};

export default function ReuseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getUser();
  const [listing, setListing] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestMsg, setRequestMsg] = useState("");
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get(`/reuse-listings/${id}`, { params: user ? { user_id: user.id } : {} });
      setListing(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Listing not found");
      navigate("/reuse");
      return;
    }
    if (user) {
      try {
        const { data: reqs } = await api.get(`/reuse-listings/${id}/requests`, { params: { user_id: user.id } });
        setRequests(reqs);
      } catch {
        setRequests([]);
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!listing) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-5 py-20 text-center text-[#6B6965]">Loading…</div>
      </Layout>
    );
  }

  const isOwner = !!user && user.id === listing.owner_id;
  const myRequest = !isOwner ? requests.find((r) => r.requester_id === user?.id) : null;
  const acceptedRequest = requests.find((r) => r.status === "accepted");
  const pill = STATUS_PILL[listing.status] || STATUS_PILL.available;
  const gallery = listing.image_urls?.length ? listing.image_urls : [];

  const submitRequest = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (requestMsg.trim().length < 3) {
      toast.error("Add a short message so the owner knows a bit about you");
      return;
    }
    setSubmittingRequest(true);
    try {
      await api.post(`/reuse-listings/${id}/requests`, { message: requestMsg.trim() }, { params: { user_id: user.id } });
      toast.success("Request sent — the owner will get back to you soon.");
      setRequestOpen(false);
      setRequestMsg("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send request");
    } finally {
      setSubmittingRequest(false);
    }
  };

  const actionRequest = async (reqId, action) => {
    try {
      await api.patch(`/reuse-listings/${id}/requests/${reqId}`, { action }, { params: { user_id: user.id } });
      toast.success(
        action === "accept" ? "Accepted — you can now message them to arrange pickup." :
        action === "decline" ? "Request declined." :
        "Request withdrawn."
      );      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const markTaken = async () => {
    try {
      await api.patch(`/reuse-listings/${id}`, { status: "taken" }, { params: { user_id: user.id } });
      toast.success("Marked as taken — thanks for keeping it out of landfill!");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteListing = async () => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    try {
      await api.delete(`/reuse-listings/${id}`, { params: { user_id: user.id } });
      toast.success("Listing deleted.");
      navigate("/reuse");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // ChatPanel context — visible once a request is accepted, connecting the
  // requester and the owner. Uses the existing /messages endpoint with the
  // new reuse_listing_id context added in messaging.py.
  const chatPeer = (() => {
    if (!user) return null;
    if (isOwner && acceptedRequest) {
      return { id: acceptedRequest.requester_id, name: acceptedRequest.requester_name };
    }
    if (myRequest?.status === "accepted") {
      return { id: listing.owner_id, name: listing.owner_name };
    }
    return null;
  })();

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <Button
          data-testid="reuse-back"
          variant="ghost"
          onClick={() => navigate("/reuse")}
          className="rounded-full text-[#1B3624] hover:bg-[#EBE8E0] mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Reuse
        </Button>

        <Card className="rounded-3xl border-[#E2DFD8] bg-white overflow-hidden">
          {/* Photo carousel */}
          {gallery.length > 0 && (
            <div data-testid="reuse-gallery" className="relative bg-[#F6F4F0]">
              <img
                src={gallery[carouselIdx]}
                alt=""
                className="w-full max-h-[420px] object-cover"
              />
              {gallery.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {gallery.map((_, i) => (
                    <button
                      key={i}
                      data-testid={`reuse-gallery-dot-${i}`}
                      onClick={() => setCarouselIdx(i)}
                      className={`w-2 h-2 rounded-full transition ${i === carouselIdx ? "bg-white" : "bg-white/50"}`}
                      aria-label={`Photo ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px]">{listing.category}</Badge>
              <Badge className="bg-[#4A6B56] text-white hover:bg-[#4A6B56] uppercase text-[10px]">Free</Badge>
              <Badge data-testid="reuse-status-pill" className={`${pill.cls} hover:${pill.cls} uppercase text-[10px]`}>{pill.label}</Badge>
              {listing.condition && (
                <span className="text-xs text-[#6B6965]">Condition: <span className="text-[#1B3624] font-medium">{CONDITION_LABEL[listing.condition] || listing.condition}</span></span>
              )}
            </div>
            <h1
              data-testid="reuse-detail-title"
              className="mt-3 text-3xl sm:text-4xl tracking-tighter font-medium"
              style={{ fontFamily: "Outfit" }}
            >
              {listing.title}
            </h1>
            <div className="mt-3 text-sm text-[#6B6965] flex flex-wrap gap-x-6 gap-y-1">
              <span data-testid="reuse-postcode-area" className="flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {listing.postcode_area} <span className="text-[#9B9893]">· approx location only</span>
              </span>
              <span>Pickup: <span className="text-[#1B3624]">{listing.pickup_window}</span></span>
              {listing.preferred_date && <span>Preferred date: {listing.preferred_date}</span>}
            </div>
            <p className="mt-4 text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">{listing.description}</p>
            <div className="mt-4 text-sm text-[#6B6965]">
              Listed by <span className="text-[#1B3624] font-medium">{listing.owner_name}</span>
            </div>

            {/* Action row — depends on role */}
            <div className="mt-6 flex flex-wrap gap-2">
              {!isOwner && listing.status === "available" && !myRequest && (
                <Button
                  data-testid="reuse-request-btn"
                  onClick={() => (user ? setRequestOpen(true) : navigate("/login"))}
                  className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
                >
                  <Send className="w-4 h-4 mr-1" /> Request this item
                </Button>
              )}
              {!isOwner && myRequest && (
                <div data-testid="reuse-my-request-status" className="rounded-full bg-[#EBE8E0] text-[#1B3624] px-4 py-2 text-xs uppercase tracking-widest">
                  Your request: {myRequest.status}
                </div>
              )}
              {!isOwner && myRequest && ["pending", "accepted"].includes(myRequest.status) && (
                <Button
                  data-testid="reuse-withdraw-btn"
                  variant="outline"
                  onClick={() => actionRequest(myRequest.id, "withdraw")}
                  className="rounded-full border-[#E2DFD8] text-[#1B3624]"
                >
                  Withdraw request
                </Button>
              )}
              {!isOwner && user && (
                <>
                  <Button
                    data-testid="reuse-report-btn"
                    variant="ghost"
                    onClick={() => setReportOpen(true)}
                    className="rounded-full text-[#6B6965] hover:text-[#D67B54]"
                  >
                    <Flag className="w-4 h-4 mr-1" /> Report
                  </Button>
                  <BlockUserButton
                    currentUserId={user.id}
                    targetUserId={listing.owner_id}
                    targetName={listing.owner_name}
                    onBlocked={() => navigate("/reuse")}
                  />
                </>
              )}
              {isOwner && listing.status !== "taken" && (
                <Button
                  data-testid="reuse-mark-taken-btn"
                  onClick={markTaken}
                  className="rounded-full bg-[#4A6B56] hover:bg-[#3d5947] text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Mark as taken
                </Button>
              )}
              {isOwner && (
                <Button
                  data-testid="reuse-delete-btn"
                  variant="ghost"
                  onClick={deleteListing}
                  className="rounded-full text-[#6B6965] hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Owner-only: incoming requests */}
        {isOwner && (
          <section data-testid="reuse-owner-requests" className="mt-8">
            <h2 className="text-xl tracking-tight font-medium mb-3" style={{ fontFamily: "Outfit" }}>
              Requests ({requests.length})
            </h2>
            {requests.length === 0 ? (
              <Card className="p-6 rounded-2xl border-dashed border-[#E2DFD8] bg-transparent text-center text-[#6B6965]">
                No requests yet — neighbours will see this in the browse feed and reach out.
              </Card>
            ) : (
              <div className="space-y-3">
                {requests.map((r) => (
                  <Card key={r.id} data-testid={`reuse-req-${r.id}`} className="p-4 rounded-2xl border-[#E2DFD8]">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-medium" style={{ fontFamily: "Outfit" }}>{r.requester_name}</div>
                        <div className="text-xs text-[#6B6965] uppercase tracking-widest mt-0.5">Status: {r.status}</div>
                      </div>
                      {r.status === "pending" && (
                        <div className="flex gap-2">
                          <Button
                            data-testid={`reuse-accept-${r.id}`}
                            onClick={() => actionRequest(r.id, "accept")}
                            className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Accept
                          </Button>
                          <Button
                            data-testid={`reuse-decline-${r.id}`}
                            variant="outline"
                            onClick={() => actionRequest(r.id, "decline")}
                            className="rounded-full border-[#E2DFD8] text-[#6B6965]"
                          >
                            <XIcon className="w-4 h-4 mr-1" /> Decline
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="mt-3 text-sm text-[#1A1A1A] whitespace-pre-wrap">{r.message}</p>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Chat — only rendered once a request is accepted (owner ⟷ picked requester) */}
        {chatPeer && (
          <section data-testid="reuse-chat-section" className="mt-8">
            <h2 className="text-xl tracking-tight font-medium mb-3" style={{ fontFamily: "Outfit" }}>Arrange pickup</h2>
            <ChatPanel
              currentUserId={user.id}
              peerId={chatPeer.id}
              peerName={chatPeer.name}
              reuseListingId={listing.id}
              viewerRole={user.role}
              testIdPrefix="reuse-chat"
            />
          </section>
        )}

        {/* Approx-pin map — admin-only preview. Regular users see the
            postcode-area chip earlier on the page but no map, per product decision. */}
        {isAdminUser(user) && listing.lat && listing.lng && (
          <div className="mt-8">
            <div className="text-sm text-[#6B6965] mb-2">Approximate pickup location (±150 m)</div>
            <MapView
              markers={[{ id: listing.id, lat: listing.lat, lng: listing.lng, title: listing.title, subtitle: listing.postcode_area }]}
              center={[listing.lat, listing.lng]}
              zoom={13}
              height="h-[320px]"
              testId="reuse-detail-map"
            />
          </div>
        )}
      </div>

      {/* Request modal */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent data-testid="reuse-request-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Recycle className="w-4 h-4 inline text-[#4A6B56] mr-1" /> Request this item
            </DialogTitle>
            <DialogDescription>
              Send a short note to {listing.owner_name}. They'll approve you, then you can arrange pickup in-app.
              We never share your exact address with anyone until you agree.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="reuse-request-msg"
            value={requestMsg}
            onChange={(e) => setRequestMsg(e.target.value)}
            placeholder="Hi! I can collect Saturday morning if that works…"
            maxLength={500}
            className="rounded-xl min-h-[100px]"
          />
          <DialogFooter>
            <Button
              data-testid="reuse-request-cancel"
              variant="ghost"
              onClick={() => setRequestOpen(false)}
              className="rounded-full text-[#6B6965]"
            >
              Cancel
            </Button>
            <Button
              data-testid="reuse-request-send"
              onClick={submitRequest}
              disabled={submittingRequest || requestMsg.trim().length < 3}
              className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
            >
              {submittingRequest ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {reportOpen && user && (
        <ReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          userId={user.id}
          targetKind="reuse_listing"
          targetId={listing.id}
          targetLabel={listing.title}
        />
      )}
    </Layout>
  );
}
